import { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { TrendingUp, Wallet } from "lucide-react";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function ChartTooltip({ active, payload, label, prefix }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-[#0D0D0D]/95 px-3 py-2 shadow-lg">
      <p className="text-[11px] text-white/50">{label}</p>
      <p className="text-sm font-bold text-[#E91E8C]">{prefix}{Number(payload[0].value).toLocaleString("it-IT", { minimumFractionDigits: prefix === "€" ? 2 : 0, maximumFractionDigits: 2 })}</p>
    </div>
  );
}

export default function CreatorCharts({ transactions = [] }) {
  const { series, totalNet, totalCount } = useMemo(() => {
    const DAYS = 30;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const buckets = new Map();
    const order = [];
    for (let i = DAYS - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = ymd(d);
      buckets.set(key, { key, label: d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }), net: 0, count: 0 });
      order.push(key);
    }
    let totalNet = 0, totalCount = 0;
    for (const t of transactions) {
      const d = new Date(t.created_at);
      if (isNaN(d)) continue;
      const key = ymd(d);
      const b = buckets.get(key);
      const net = Number(t.net) || 0;
      totalNet += net;
      totalCount += 1;
      if (b) { b.net += net; b.count += 1; }
    }
    let cum = 0;
    const series = order.map((k) => {
      const b = buckets.get(k);
      cum += b.net;
      return { ...b, net: Math.round(b.net * 100) / 100, cumulative: Math.round(cum * 100) / 100 };
    });
    return { series, totalNet: Math.round(totalNet * 100) / 100, totalCount };
  }, [transactions]);

  const tickEvery = (v, i) => (i % 6 === 0 ? v : "");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8" data-testid="creator-charts">
      {/* Daily net earnings */}
      <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-5 sm:p-6" data-testid="chart-earnings">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-[#E91E8C]" />
            <h3 className="text-sm font-bold font-heading">Guadagni · 30 giorni</h3>
          </div>
          <span className="text-lg font-black font-heading text-[#E91E8C]">€{totalNet.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={series} margin={{ top: 5, right: 6, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="gradNet" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E91E8C" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#E91E8C" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={tickEvery} interval={0} minTickGap={0} />
            <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }} tickLine={false} axisLine={false} width={40} />
            <Tooltip content={<ChartTooltip prefix="€" />} cursor={{ stroke: "#E91E8C", strokeOpacity: 0.2 }} />
            <Area type="monotone" dataKey="net" stroke="#E91E8C" strokeWidth={2.5} fill="url(#gradNet)" animationDuration={1200} animationEasing="ease-out" dot={false} activeDot={{ r: 4, fill: "#E91E8C" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Cumulative revenue */}
      <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-5 sm:p-6" data-testid="chart-cumulative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#F5C6D6]" />
            <h3 className="text-sm font-bold font-heading">Ricavi cumulati</h3>
          </div>
          <span className="text-lg font-black font-heading text-[#F5C6D6]">{totalCount} transazioni</span>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={series} margin={{ top: 5, right: 6, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="gradCum" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F5C6D6" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#F5C6D6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={tickEvery} interval={0} minTickGap={0} />
            <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }} tickLine={false} axisLine={false} width={40} />
            <Tooltip content={<ChartTooltip prefix="€" />} cursor={{ stroke: "#F5C6D6", strokeOpacity: 0.2 }} />
            <Area type="monotone" dataKey="cumulative" stroke="#F5C6D6" strokeWidth={2.5} fill="url(#gradCum)" animationDuration={1400} animationEasing="ease-out" dot={false} activeDot={{ r: 4, fill: "#F5C6D6" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
